/**
 * The start-of-care slots for a referral, from **the product's own scheduler**.
 *
 * Until 2026-09-24 the referral panel answered "who could take this start of
 * care?" with a search written for the demo, over the demo's own idea of a
 * clinician's day. On 2026-09-23 that answer was *no SOC slots available* for a
 * patient two nurses could have seen, because the search counts the demo's
 * reservations as walls and two of them had settled on the mornings the seed
 * keeps clear. The real scheduler had slots the whole time.
 *
 * So the panel asks `scheduleOption`, the same query and the same service the
 * product's scheduler screen uses — which means the two agree, and that screen
 * can be opened beside the panel to show how the answer was reached.
 *
 * **The case's own traits are not passed as a filter.** The scheduler would
 * happily narrow on them, but then the scheduler screen opened next to the panel
 * would show a larger set than the panel did and nobody could say why. The
 * service returns what it returns; `selectReferralChecks` narrows it against the
 * roster, so the panel showing fewer is a sentence somebody can say out loud —
 * *this case needs a wound-VAC nurse*.
 */
import { useEffect, useMemo } from "react";
import { useSchedulerOptionsLazyQuery, useSchedulerProvidersListLazyQuery } from "~/graphql/__generated__/graphql";
import { dateKeyAt } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { holdWindowDays, type CandidateSlot, type ProviderEligibility } from "../../engine/referralChecks";
import type { DemoState } from "@caredna-marlin/utils/homeHealthDemo/state";

/**
 * **What the scheduler said about a referral, once somebody has opened it.**
 *
 * The queue cannot ask — that would run the optimizer once per row — so until a
 * referral has been looked at, the list has no answer to show and says nothing
 * rather than guessing (Pete, 2026-09-24). Opening the panel asks, and the
 * answer is kept here so the row can carry it when you come back.
 *
 * Deliberately not in the store: it is what a service said a moment ago, not
 * demo state, and it should not survive a reload or be reset with anything else.
 */
const answers = new Map<string, boolean>();

/** True when the scheduler offered somebody, false when it had nobody, absent when nobody asked. */
export function schedulerFoundSlot(referralId: string): boolean | undefined {
  return answers.get(referralId);
}

export interface SocScheduleOptions {
  /** Best slot per clinician, ready for `selectReferralChecks`. */
  slots: CandidateSlot[];
  /**
   * What the scheduler says about each clinician, by provider id. It is what
   * tells *Not working* from *No capacity* in the drawer — the distinction this
   * file's own search got wrong, because it reads the demo's reservations as a
   * full day.
   */
  eligibility: Record<number, ProviderEligibility>;
  /** The service was asked and has answered — even if the answer was none. */
  answered: boolean;
  loading: boolean;
  error?: string;
}

export function useSocScheduleOptions(state: DemoState, referralId: string, today: string): SocScheduleOptions {
  const referral = state.referrals[referralId];
  const tz = state.config.branch.timeZone;
  const b = state.meta.bindings;
  const patientId = referral ? b.patientIdsByRef[referral.patientRef] : undefined;
  const visitTypeId = b.visitTypeIdsByKind.SOC;
  const serviceLineId = b.serviceLineId;
  const days = referral ? holdWindowDays(referral, today) : [];

  // The window the SOC has to land in, as the service wants it: an instant for
  // the first morning and one for the last evening. `targetTime` is what the
  // scheduler ranks proximity against, and the branch wants it as early in the
  // window as it can have it.
  const startTime = days.length ? dateKeyAt(days[0], "00:00", tz).toISOString() : undefined;
  const endTime = days.length ? dateKeyAt(days[days.length - 1], "23:59", tz).toISOString() : undefined;

  const duration = state.config.visitTypes.SOC.plannedMinutes;
  /**
   * **Lazy, not skipped.** `useQuery` with `skip` still wants a complete
   * variables object, and an unbound tenant has no patient or service line to
   * put in one; handing it undefined for a non-null variable trips an Apollo
   * invariant before `skip` is ever consulted. Firing it ourselves once the ids
   * exist is also the plainer reading of "ask when the panel opens".
   */
  const [run, { data, loading, error, called }] = useSchedulerOptionsLazyQuery({ fetchPolicy: "cache-and-network" });
  const [runProviders, { data: providerData }] = useSchedulerProvidersListLazyQuery({ fetchPolicy: "cache-and-network" });
  const ready = patientId !== undefined && visitTypeId !== undefined && serviceLineId !== undefined && !!startTime && !!endTime;
  useEffect(() => {
    if (!ready) return;
    void run({
      variables: {
        patientId,
        startTime,
        // The branch wants it as early in the window as it can have it, so the
        // hour proximity is scored against is the window's own start.
        targetTime: startTime,
        endTime,
        serviceLineId,
        organizationalUnitId: null,
        visitTypeId,
        duration,
        serviceIds: [],
        // Not a filter, deliberately — see the note at the top of this file.
        providerTraits: [],
        // Any day and any hour inside the window; the demo's own rules about
        // orientation and declared days narrow it afterwards.
        daysOfWeek: null,
        hoursOfDay: null,
        reschedulingVisitId: null,
        selectedProviderIds: null,
        useOverflowShifts: false,
      },
    });
    void runProviders({ variables: { patientId, startTime, endTime, serviceLineId, organizationalUnitId: null, visitTypeId } });
  }, [ready, run, runProviders, patientId, visitTypeId, serviceLineId, startTime, endTime, duration]);

  const eligibility = useMemo(() => {
    const out: Record<number, ProviderEligibility> = {};
    for (const p of providerData?.scheduleProvidersList ?? []) {
      out[p.id] = {
        hasServiceLine: p.hasServiceLine,
        hasProviderType: p.hasProviderType,
        // Nullable on the schema: a provider type that needs no licence has
        // nothing to say here, which is not the same as failing the check.
        hasStateLicense: p.hasStateLicense !== false,
        hasNearbyShifts: p.hasNearbyShifts,
        hasNonOverflowShifts: p.hasNonOverflowShifts,
      };
    }
    return out;
  }, [providerData]);

  const slots = useMemo(() => {
    const best = new Map<number, CandidateSlot>();
    for (const o of data?.scheduleOption?.options ?? []) {
      // A start of care is one clinician; the first resource is whose day it is.
      const providerId = o.resources[0]?.providerId;
      if (providerId === undefined) continue;
      const slot: CandidateSlot = {
        providerId,
        startIso: o.startTime,
        endIso: o.endTime,
        arrivalWindowStartIso: o.arrivalWindowStart,
        arrivalWindowEndIso: o.arrivalWindowEnd,
        originDriveMinutes: o.originDrive,
        destinationDriveMinutes: o.destinationDrive,
        totalScore: o.scores.totalScore,
        rankCategory: o.rankCategory,
      };
      const prev = best.get(providerId);
      // **Best per clinician, not best overall.** The panel is one row per person,
      // so trimming to the top N slots would drop a clinician who has one good
      // hour behind another who has six. The roster bounds the rows at the
      // handful of people who can assess.
      if (!prev || slot.totalScore > prev.totalScore) best.set(providerId, slot);
    }
    return [...best.values()];
  }, [data]);

  /**
   * **An error is an answer, not a reason to ask somebody else.** Once the
   * service has been asked and has come back — with options, with none, or with
   * a failure — its reply is what the panel shows. Treating a failure as "not
   * asked" would hand the question back to the local search, which is the silent
   * second opinion this whole change exists to remove.
   *
   * Not asking at all is different: with no service line or no patient bound
   * there is nothing to ask, so the selector keeps the reading it always had.
   */
  const answered = called && !loading;
  if (answered) answers.set(referralId, slots.length > 0);
  return { slots, eligibility, answered, loading, error: error?.message };
}
